import { useEffect, useState } from 'react';
import OgShell from '../components/OgShell';
import { fetchWitnesses, getPublicWitnessMediaUrl, type PublicWitness } from '../lib/api';
import { formatDate } from '../lib/constants';

const STAGE_LABELS: Record<string, string> = {
  before: '施炁前',
  pre_condition: '施炁前',
  during: '施炁當下',
  method: '施炁當下｜施作方式',
  immediate_reaction: '施炁當下｜即時反應',
  after: '施炁後',
  post_condition: '施炁後',
  within24h: '施炁後（24 小時內）',
  followup_24h: '施炁後（24 小時內）',
};

const FIELD_LABELS: Record<string, string> = {
  main_issue: '主要問題', body_parts: '身體部位', discomfort: '身體不適程度',
  emotion: '情緒狀態', function: '生活功能', life_function: '生活功能',
  history: '病史／過往事件', treatment: '歷史背景', start: '開始時間',
  end: '結束時間', contact: '接觸方式', position: '施炁位置',
  audio: '音頻／設備', stopped: '是否中途停止', attention: '需注意反應',
  reactions: '當下反應', other: '其他', duration: '持續時間',
  recipient_note: '受施者描述', recipient_change: '本人感受到的變化',
  new_discomfort: '新不適', need_help: '需專業協助', next_step: '下一步建議',
  followup_24h: '24 小時內回報', outcome: '原有問題', new_reactions: '新反應',
  other_therapy: '其他療法', before: '施炁前狀況', during: '施炁當下感受', after: '施炁後變化',
};

export default function WitnessesPage() {
  const [witnesses, setWitnesses] = useState<PublicWitness[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchWitnesses().then((data) => { if (!cancelled) setWitnesses(data); })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : '載入失敗'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <OgShell>
      <div className="max-w-4xl mx-auto px-4 md:px-6 space-y-6">
        <header className="text-center space-y-2 pt-4">
          <h1 className="text-xl md:text-2xl text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>匿名見證</h1>
          <p className="text-sm text-foreground-500 max-w-2xl mx-auto">
            完整案例已移除姓名、聯絡方式與其他私人識別資料；露臉或可辨識的照片、影片僅於案主逐檔同意後公開。
          </p>
        </header>

        {loading ? <Loading /> : error ? (
          <div className="py-16 text-center"><p className="text-sm text-rose-600">{error}</p></div>
        ) : witnesses.length === 0 ? (
          <div className="py-16 text-center bg-background-100 border border-foreground-200/50 rounded-xl">
            <i className="ri-eye-off-line text-2xl text-foreground-300" />
            <p className="text-sm text-foreground-500 mt-2">目前尚無公開見證。</p>
          </div>
        ) : (
          <>
            <WitnessDirectory witnesses={witnesses} />
            <div className="space-y-6">{witnesses.map((w) => <WitnessCard key={w.id} witness={w} />)}</div>
          </>
        )}
      </div>
    </OgShell>
  );
}

function WitnessCard({ witness: w }: { witness: PublicWitness }) {
  const sessions = w.sessions || [];
  const records = w.records || [];
  const media = w.media || [];

  return (
    <article id={witnessAnchor(w)} className="scroll-mt-24 bg-background-100 border border-foreground-200/50 rounded-xl p-5 md:p-6 space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap border-b border-foreground-200/50 pb-4">
        <div>
          <h2 className="text-lg text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>{w.case_code || '匿名見證'}</h2>
          <p className="text-xs text-foreground-400 mt-1">
            {w.session_datetime ? formatDate(w.session_datetime) : '施炁日期未填'}{w.location ? ` · ${w.location}` : ''}
          </p>
        </div>
        {w.evidence_level && <span className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900">Evidence {w.evidence_level}</span>}
      </div>

      <section className="space-y-3">
        <h3 className="text-base font-medium text-foreground-800">施炁紀錄（{sessions.length || 1} 次）</h3>
        {(sessions.length ? sessions : [{ id: null, session_number: 1, session_datetime: w.session_datetime }]).map((session, index) => {
          const sid = numberValue(session.id);
          const sessionRecords = records.filter((record) => {
            const recordSid = numberValue(record.session_id);
            return sid === null ? recordSid === null || sessions.length <= 1 : recordSid === sid;
          });
          return (
            <div key={String(session.id ?? index)} className="rounded-lg border border-foreground-200/60 bg-background-50 p-4 space-y-4">
              <h4 className="text-sm font-medium text-foreground-800">
                第 {String(session.session_number ?? index + 1)} 次施炁 · {session.session_datetime ? formatDate(String(session.session_datetime)) : '—'}
              </h4>
              {session.method ? <p className="text-sm text-foreground-700">施炁方式：{String(session.method)}</p> : null}
              {session.practitioner_observation ? <p className="text-sm text-foreground-700 whitespace-pre-line">施炁者觀察：{String(session.practitioner_observation)}</p> : null}
              {sessionRecords.length ? sessionRecords.map((record) => <RecordBlock key={String(record.id)} record={record} />) : <p className="text-xs text-foreground-400">本次尚無分段文字紀錄。</p>}
            </div>
          );
        })}
      </section>

      {media.length > 0 && <PublicMedia items={media} />}

      <section className="rounded-lg bg-background-200/60 p-4 space-y-1.5">
        <h3 className="text-sm font-medium text-foreground-800">Reviewer 覆核</h3>
        <p className="text-xs text-foreground-600">覆核狀態：{w.review_status || 'approved'}</p>
        <p className="text-xs text-foreground-600">Evidence：{w.evidence_level || 'E0'}</p>
        <p className="text-xs text-foreground-400">發布：{w.published_at ? formatDate(w.published_at) : '—'}</p>
      </section>
    </article>
  );
}

function WitnessDirectory({ witnesses }: { witnesses: PublicWitness[] }) {
  return (
    <nav aria-label="見證案例目錄" className="rounded-xl border border-foreground-200/60 bg-background-100 p-4 md:p-5 space-y-3">
      <div>
        <h2 className="text-base font-medium text-foreground-900">見證案例目錄</h2>
        <p className="text-xs text-foreground-500 mt-1">點選案號或內容，可直接前往完整案例。</p>
      </div>
      <div className="overflow-x-auto rounded-lg border border-foreground-200/60 bg-background-50">
        <table className="w-full min-w-[720px] text-left">
          <thead className="bg-background-200/70 text-xs text-foreground-500">
            <tr>
              <th className="px-3 py-2.5 font-medium w-44">案號</th>
              <th className="px-3 py-2.5 font-medium">主訴</th>
              <th className="px-3 py-2.5 font-medium">歷史背景</th>
              <th className="px-3 py-2.5 font-medium w-24 text-center">施炁次數</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-foreground-200/50">
            {witnesses.map((witness) => {
              const index = witnessIndexText(witness);
              return (
                <tr key={witness.id} className="hover:bg-accent-50/60 transition-colors">
                  <td className="p-0 align-top">
                    <a href={`#${witnessAnchor(witness)}`} className="block px-3 py-3 text-sm font-medium text-accent-700 hover:underline">
                      {witness.case_code || `案例 #${witness.id}`}
                    </a>
                  </td>
                  <td className="p-0 align-top">
                    <a href={`#${witnessAnchor(witness)}`} className="block px-3 py-3 text-sm text-foreground-700 leading-relaxed">
                      {index.complaint}
                    </a>
                  </td>
                  <td className="p-0 align-top">
                    <a href={`#${witnessAnchor(witness)}`} className="block px-3 py-3 text-sm text-foreground-600 leading-relaxed">
                      {index.background}
                    </a>
                  </td>
                  <td className="p-0 align-top text-center">
                    <a href={`#${witnessAnchor(witness)}`} className="block px-3 py-3 text-sm font-medium text-foreground-700">
                      {witness.sessions?.length || 1} 次
                    </a>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </nav>
  );
}

function witnessAnchor(witness: PublicWitness): string {
  return `witness-${witness.case_code || witness.id}`;
}

function witnessIndexText(witness: PublicWitness): { complaint: string; background: string } {
  const records = witness.records || [];
  const before = records.find((record) => ['before', 'pre_condition'].includes(String(record.record_stage || '')));
  const data = objectValue(before?.structured_data);
  const complaint = cleanIndexValue(data.main_issue) || cleanIndexValue(before?.narrative) || '未填主訴';
  const backgroundParts = [cleanIndexValue(data.history), cleanIndexValue(data.treatment)].filter(Boolean);
  return {
    complaint,
    background: Array.from(new Set(backgroundParts)).join('；') || '未填歷史背景',
  };
}

function cleanIndexValue(value: unknown): string {
  if (Array.isArray(value)) return value.map(String).filter(Boolean).join('、');
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

function RecordBlock({ record }: { record: Record<string, unknown> }) {
  const stage = String(record.record_stage || '紀錄');
  return (
    <div className="space-y-2 border-t first:border-t-0 border-foreground-200/50 pt-3 first:pt-0">
      <h5 className="text-sm font-medium text-foreground-700">{STAGE_LABELS[stage] || stage}</h5>
      {record.narrative ? <p className="text-sm text-foreground-700 whitespace-pre-line">{String(record.narrative)}</p> : null}
      <StructuredData data={objectValue(record.structured_data)} />
    </div>
  );
}

function StructuredData({ data }: { data: Record<string, unknown> }) {
  const entries = Object.entries(data).filter(([, value]) => value !== null && value !== undefined && value !== '' && !(Array.isArray(value) && value.length === 0));
  if (!entries.length) return null;
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-1.5">
      {entries.map(([key, value]) => (
        <div key={key} className="text-sm">
          <dt className="inline text-foreground-400">{FIELD_LABELS[key] || key}：</dt>
          <dd className="inline text-foreground-700">{displayValue(value, key)}</dd>
        </div>
      ))}
    </dl>
  );
}

function PublicMedia({ items }: { items: Array<Record<string, unknown>> }) {
  const [urls, setUrls] = useState<Record<string, string>>({});
  useEffect(() => {
    let active = true;
    const created: string[] = [];
    Promise.all(items.map(async (item) => {
      const path = String(item.file_path || '');
      const url = path ? await getPublicWitnessMediaUrl(path) : null;
      if (url) created.push(url);
      return [String(item.id), url] as const;
    })).then((pairs) => {
      if (active) setUrls(Object.fromEntries(pairs.filter((pair): pair is readonly [string, string] => Boolean(pair[1]))));
    });
    return () => { active = false; created.forEach((url) => URL.revokeObjectURL(url)); };
  }, [items]);

  return (
    <section className="space-y-3">
      <h3 className="text-base font-medium text-foreground-800">公開照片／影片／文件</h3>
      <p className="text-xs text-foreground-500">只有露臉或含可辨識資訊的照片、影片需要案主逐檔同意。</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {items.map((item) => {
          const url = urls[String(item.id)];
          const type = String(item.file_type || 'file');
          return (
            <div key={String(item.id)} className="rounded-lg border border-foreground-200/60 bg-background-50 p-3 space-y-2">
              {url && type === 'image' ? <img src={url} alt="公開案例附件" className="w-full max-h-72 object-contain rounded" /> : null}
              {url && type === 'video' ? <video src={url} controls playsInline className="w-full max-h-72 rounded bg-black" /> : null}
              {url && type === 'audio' ? <audio src={url} controls className="w-full" /> : null}
              {url && !['image', 'video', 'audio'].includes(type) ? <a href={url} download className="text-sm text-accent-700 hover:underline">開啟公開文件</a> : null}
              {!url ? <p className="text-xs text-foreground-400">附件載入中或暫時無法顯示</p> : null}
              {item.description ? <p className="text-sm text-foreground-600 whitespace-pre-line">{String(item.description)}</p> : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Loading() {
  return <div className="py-20 text-center"><div className="w-10 h-10 mx-auto border-2 border-accent-300 border-t-accent-500 rounded-full animate-spin" /></div>;
}

function objectValue(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function numberValue(value: unknown): number | null {
  return typeof value === 'number' ? value : value === null || value === undefined ? null : Number(value);
}

function displayValue(value: unknown, key: string): string {
  if (typeof value === 'boolean') return value ? '是' : '否';
  if (Array.isArray(value)) return value.map(String).join('、');
  if (typeof value === 'number' && ['discomfort', 'emotion', 'function', 'life_function'].includes(key)) return `${value} / 10`;
  if (value && typeof value === 'object') return Object.values(value as Record<string, unknown>).map(String).join('、');
  return String(value);
}
